import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Grid,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableContainer,
  TableRow,
  TableBody,
  TableCell
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { customAlphabet } from "nanoid";
import { putJobOrder } from "../API/jobOrderAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import {
  normalizeJobTypeKeyToLabel,
  normalizeJobDescriptionKeyToLabel,
  normalizeDocumentKeyToLabel,
  normalizeEquipmentKeyToLabel,
} from "../JobOrders/utils/JobOrderConstants";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

const convertToAmPm = (time24) => {
  const [hour, minute] = time24.split(':');
  const hourNum = parseInt(hour, 10);
  const suffix = hourNum >= 12 ? 'PM' : 'AM';
  const hour12 = hourNum % 12 || 12;
  return `${hour12}:${minute} ${suffix}`;
};

// Helper function to convert "dd-mmm-yyyy" to "yyyy-mm-dd"
const convertToISO = (dateStr) => {
  // Example: "01-Mar-2027"
  const [day, mon, year] = dateStr.split("-");
  const monthMapping = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };
  return `${year}-${monthMapping[mon]}-${day}`;
};

// helper for Job Order Constants
const formatList = (arr, normalizer) =>
  Array.isArray(arr) ? arr.map(normalizer).join(", ") : "";
// const job_types = [
//   "GPC",
//   "Fogging",
//   "Monitoring",
//   "Deep Cleaning",
//   "QTM",
//   "Heat Treatment",
//   "Vacuuming",
//   "Termite Control",
//   "Comprehensive Termite",
//   "Termite Baiting",
//   "Termite Spot Spraying",
//   "AG Installation",
//   "Phase 1 and Phase 2",
//   "QPMS",
//   "Ant Control",
//   "Cockroach Control",
//   "Mosquito Control",
//   "Cockroach Gel Bait/Powder",
//   "Basic Rodent Control",
//   "Intensive Rodent Control",
//   "Termite Inspection",
//   "Disinfection",
//   "Billing",
//   "Collection",
//   "Deliver Service Invoice",
//   "Deliver Certificate",
//   "QuickCert",
//   "Ocular",
//   "Rodent Monitoring",
//   "Daily Monitoring",
//   "Logistics",
//   "ILT Monitoring",
//   "Facility Audit",
//   "Meeting/Seminar",
//   "QA Visit",
//   "Termipore Installation",
//   "In2Care Installation",
//   "In2Care Monitoring",
//   "Exterra Inground",
//   "Delivery of Materials",
//   "Backjob"
// ]

// const job_description = [
//   "Spraying",
//   "Misting",
//   "Fogging",
//   "Mistblower",
//   "Rodent Trapping",
//   "Rodent Monitoring",
//   "Logistics (Billing, Collection, Delivery)",
//   "AG Installation",
//   "Comprehensive Termite Control",
//   "Termatrac Monitoring",
//   "Wiping",
//   "Disinfection",
//   "UV Light",
//   "Cold Fogging",
//   "AG Monitoring",
//   "Powder/Gel Baiting",
//   "Soil Poisoning",
//   "AG Pullout",
//   "QA Visit/Facility Audit",
//   "GPC with Blower and Vacuum",
//   "Termipore, In2Care, or Device Installation",
//   "In2Care Monitoring"
// ]

// const documents_to_bring = [
//   'Contract',
//   'Service Report',
//   'Company Cellphone',
//   'TSDR',
//   'Monitoring Forms',
//   'Certificate',
//   'Acknowledgement Form',
//   'Delivery Receipt',
//   'Service Invoice',
//   'Collection Receipt',
//   'SOA',
//   'Traceability Form',
//   'Billing Statement',
//   'Work Permit / Gate Pass',
//   'Facility Audit Form',
//   'Facility Map',
//   'Chemical SDS, CPR',
//   'Audit Docs'
// ]

// const equipment = [
//   'Misting',
//   'Sprayer',
//   'Handsprayer',
//   'Mist Blower',
//   'Fogging Machine',
//   'Flashlight',
//   'Screwdriver',
//   'Masking/Duct Tape',
//   'Distilled Water',
//   'No more nails',
//   'Label',
//   'Cartons for bosny',
//   'Gel Bait',
//   'Powder',
//   'UV Light',
//   'Steamer',
//   'Cold Fogger',
//   'Knapsack Sprayer',
//   'Termatrac',
//   'Rags',
//   'GBS',
//   'RBS',
//   'ILT',
//   'Rat Cage',
//   'Injector',
//   'Makita Drill',
//   'Power Sprayer and Hose'
// ]

function SubmitEndorsementForm() {
  const location = useLocation();
  const { group } = location.state || {};
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const generateId = customAlphabet(alphabet, 8); // Creates a function to generate an 8-character ID

  const [groupDisplay, setGroupDisplay] = useState(group);
  const [endorsementFormID, setEndorsementFormID] = useState(generateId());
  const [endorsementData, setEndorsementData] = useState({
    endorsement_form_id: endorsementFormID,
    endorsement_form_acct_tech: "",
    endorsement_form_driver: "",
    endorsement_vehicle: "",
    endorsement_dispatch_time: ""
  });
  const [endorsementList, setEndorsementList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [endorsementDataList, setEndorsementDataList] = useState(
    group?.jobOrders?.map((jobOrder) => ({
      endorsement_form_id: endorsementFormID,
      jobOrderID: jobOrder.id,
      landmark: jobOrder.landmark ?? "",
      price: jobOrder.price ?? "",
      required_chemical: jobOrder.required_chemical ?? "",
      special_notes: jobOrder.special_notes ?? "",
      number_of_visits_per_month: jobOrder.number_of_visits_per_month ?? "",
    })) || []
  );

  const handleEndorsementFieldChange = (jobOrderID, field, value) => {
    setEndorsementDataList((prevList) =>
      prevList.map((item) =>
        item.jobOrderID === jobOrderID ? { ...item, [field]: value } : item
      )
    );
  };

  const handleVehicleChange = (e) => {
    setEndorsementData((prev) => ({
      ...prev,
      endorsement_vehicle: e.target.value,
    }));
  };

  const handleDispatchTimeChange = (e) => {
    setEndorsementData((prev) => ({
      ...prev,
      endorsement_dispatch_time: e.target.value,
    }));
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (group?.jobOrders) {
      const groupWithJobScope = {
        ...group,
        jobOrders: group.jobOrders.map((job) => {
          const jobScope =
            job.competencies
              ?.filter(
                (competency) =>
                  competency !== "driving_2_wheels" &&
                  competency !== "driving_4_wheels"
              )
              .map((competency) => {
                // Format the competency name
                const formattedCompetency = competency
                  .replace(/_/g, " ") // Replace underscores with spaces
                  .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word

                // Determine specializationKey
                const specializationKey =
                  competency === "general_pest_control"
                    ? "general_pest_control"
                    : "termite";

                // Format specialization words
                const formattedSpecialization = (
                  job.specialization?.[specializationKey] || []
                ).map(
                  (word) =>
                    word.length === 2
                      ? word.toUpperCase() // Capitalize 2-letter words (e.g., "QB")
                      : word.charAt(0).toUpperCase() + word.slice(1) // Capitalize the first letter of other words
                );

                return {
                  competency: formattedCompetency,
                  specialization: formattedSpecialization,
                };
              }) || [];

          return {
            ...job,
            job_scope: jobScope, // Add job_scope to each jobOrder
          };
        }),
      };

      setGroupDisplay(groupWithJobScope); // Update the state with the modified group
    }
  }, [group]); // Trigger when the `group` prop changes

  useEffect(() => {
    if (group && group.jobOrders) {
      const list = group.jobOrders.map((jobOrder) => ({
        endorsementData, // Use the endorsementFormID or leave blank
        jobOrderID: jobOrder.id, // Use the ID from jobOrders
      }));
      setEndorsementList(list);
    }
  }, [
    group,
    endorsementData.endorsement_form_id,
    endorsementData.endorsement_form_acct_tech,
  ]);

  const handleTechnicianChange = (e) => {
    setEndorsementData((prev) => ({
      ...prev, // Spread the previous state correctly
      endorsement_form_acct_tech: e.target.value, // Update the specific field
    }));
  };


  const handleDriverChange = (e) => {
    setEndorsementData((prev) => ({
      ...prev, // Spread the previous state correctly
      endorsement_form_driver: e.target.value, // Update the specific field
    }));
  };


  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);

    console.log("Form submitted with:", endorsementDataList);

    try {
      await Promise.all(
        endorsementDataList.map(async (item) => {
          const { jobOrderID, ...otherData } = item;
          const payload = {
            ...otherData,
            endorsement_form_driver: endorsementData.endorsement_form_driver,
            endorsement_vehicle: endorsementData.endorsement_vehicle,
            endorsement_dispatch_time: endorsementData.endorsement_dispatch_time
          };
          await putJobOrder(jobOrderID, payload);
          console.log(`Updated jobOrderID: ${jobOrderID}`);
        })
      );

      setSuccessMessage("Endorsement form submitted successfully.");
      setTimeout(() => {
        navigate("/create-endorsement-form");
      }, 3000);
    } catch (error) {
      setErrorMessage(
        `An error occurred while submitting the Endorsement form: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false);
    }
  }



  return (
    <BaseFormWrapper
      titleIcon={<FactCheckIcon />}
      title="Operations"
      subTitle="Create Endorsement Form"
      subTitleRoute="/create-endorsement-form"
      formTitle="Submit Endorsement Form"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <Grid item xs={12}>
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={2}>
                  <TextField
                    label="Endorsement Form ID"
                    name="endorsementFormID"
                    value={endorsementFormID || ""} // Assuming endorsementFormID is a state or variable
                    InputLabelProps={{ shrink: true }}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="Date"
                    name="job_date"
                    type="date" // Set the type to date
                    value={
                      group.jobOrders[0]?.job_date
                        ? convertToISO(group.jobOrders[0].job_date)
                        : ""
                    } // Convert job_date back to ISO format
                    InputLabelProps={{ shrink: true }}
                    disabled
                    fullWidth
                  />
                </Grid>                
              </Grid>
            </Grid>
          </Grid>

          <StyledHr />
          
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid
                item
                xs={12}
                md={12}

              >
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={5}>
                    <FormControl fullWidth>
                      <InputLabel>Driver</InputLabel>
                      <Select
                        value={endorsementData.endorsement_form_driver}
                        name="driver"
                        onChange={handleDriverChange}
                        label="Driver"
                      >
                        {group.technicians.map((technician) => (
                          <MenuItem
                            key={technician.id}
                            value={`${technician.firstname} ${technician.lastname}`}
                          >
                            {technician.firstname} {technician.lastname}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6} >
                    <Typography variant="body1" paddingLeft={2}>
                      Technicians: {group.technicians
                        .map(technician => `${technician.firstname} ${technician.lastname}`)
                        .join(', ')}
                    </Typography>
                  </Grid>                  
                </Grid>
                {/* Vehicle and Dispatch Time */}
                <Grid container spacing={1} style={{ marginTop: 10 }}>
                  <Grid item xs={3}>
                    <TextField
                      label="Vehicle"
                      name="vehicle"
                      value={endorsementData.endorsement_vehicle}
                      onChange={handleVehicleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Dispatch Time"
                      name="dispatch_time"
                      type="time"  // Use time picker input
                      value={endorsementData.endorsement_dispatch_time}
                      onChange={handleDispatchTimeChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }} // 5 minute increments
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1} style={{ marginTop: 10 }}>
                  <Grid item xs={12}>
                    <TableContainer
                      component={Paper}
                      sx={{ boxShadow: 'none', padding: 0, margin: 0, overflowX: 'auto' }}
                    >
                      <Table
                        size="small"
                        sx={{
                          // minWidth: 3000,
                          tableLayout: 'fixed',
                          '& .MuiTableCell-root': { fontSize: '0.8rem', padding: '4px' }
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: '200px' }}>Endorser Name</TableCell>
                            <TableCell sx={{ width: '200px' }}>Endorser Contact Number</TableCell>
                            <TableCell sx={{ width: '300px' }}>Service Availed</TableCell>
                            <TableCell sx={{ width: '300px' }}>Job Description</TableCell>
                            <TableCell sx={{ width: '200px' }}>Client</TableCell>
                            <TableCell sx={{ width: '100px' }}>Date</TableCell>
                            <TableCell sx={{ width: '200px' }}>Preferred Time</TableCell>
                            <TableCell sx={{ width: '200px' }}>Address</TableCell>
                            <TableCell sx={{ width: '200px' }}>Contact Person</TableCell>
                            <TableCell sx={{ width: '250px' }}>Client Contact Details</TableCell>
                            <TableCell sx={{ width: '300px' }}>Status of Last Visit</TableCell>
                            <TableCell sx={{ width: '300px' }}>Required Documents to Bring</TableCell>
                            <TableCell sx={{ width: '100px' }}>Size in sqm</TableCell>
                            <TableCell sx={{ width: '200px' }}>Payment Method</TableCell>
                            <TableCell sx={{ width: '300px' }}>Equipment to Bring</TableCell>
                            <TableCell sx={{ width: '100px' }}>With Complaints</TableCell>
                            <TableCell sx={{ width: '100px' }}>Schedule Confirmed with Client</TableCell>
                            <TableCell sx={{ width: '300px' }}>Landmark</TableCell>
                            <TableCell sx={{ width: '200px' }}>Price</TableCell>
                            <TableCell sx={{ width: '300px' }}>Required Chemical</TableCell>
                            <TableCell sx={{ width: '300px' }}>Special Notes</TableCell>
                            <TableCell sx={{ width: '200px' }}>Number of Visits per Month</TableCell>

                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.jobOrders.map((jobOrder) => {
                            const endorsement =
                              endorsementDataList.find((item) => item.jobOrderID === jobOrder.id) || {};
                            return (
                              <TableRow key={jobOrder.id}>
                                <TableCell>
                                  {jobOrder.endorser_name || ''}
                                </TableCell>
                                <TableCell>
                                  {jobOrder.endorser_contact_number || ''}
                                </TableCell>
                                <TableCell>
                                  {formatList(jobOrder.job_types, normalizeJobTypeKeyToLabel)}
                                </TableCell>
                                <TableCell>
                                  {formatList(jobOrder.job_description, normalizeJobDescriptionKeyToLabel)}
                                </TableCell>
                                <TableCell>{jobOrder.client?.name || "No client name"}</TableCell>
                                <TableCell>
                                  {jobOrder.job_date}
                                </TableCell>
                                <TableCell>
                                  {jobOrder.start_time || "No start time"} -{' '}
                                  {jobOrder.end_time || "No end time"}
                                </TableCell>
                                <TableCell>
                                  {jobOrder.client?.address?.house_unit_street || "No street"},{' '}
                                  {jobOrder.client?.address?.barangay || "No barangay"},{' '}
                                  {jobOrder.client?.address?.city || "No city"}{' '}
                                  {jobOrder.client?.address?.metro_province || ''}
                                </TableCell>
                                <TableCell>
                                  {jobOrder.client?.contact?.firstname || ""}{' '}
                                  {jobOrder.client?.contact?.lastname || ""}
                                </TableCell>
                                <TableCell>
                                  {jobOrder.client?.contact?.number?.type || "No type"}:{' '}
                                  {jobOrder.client?.contact?.number?.number || "No number"}
                                  <br />
                                  {jobOrder.client?.contact?.email?.email || "No email"}
                                </TableCell>
                                <TableCell>
                                  {jobOrder.current_status ? (
                                    typeof jobOrder.current_status === "string" ? (
                                      jobOrder.current_status
                                        .split("_")
                                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                        .join(" ")
                                    ) : (
                                      jobOrder.current_status
                                    )
                                  ) : (
                                    ""
                                  )}
                                </TableCell>
                                <TableCell>
                                  {formatList(jobOrder.documents_to_bring, normalizeDocumentKeyToLabel)}
                                </TableCell>

                                <TableCell>{jobOrder.client?.address?.area || "No area"}</TableCell>
                                <TableCell>
                                  {jobOrder.payment_method || ""}
                                </TableCell>

                                <TableCell>
                                  {formatList(jobOrder.equipment, normalizeEquipmentKeyToLabel)}
                                </TableCell>

                                <TableCell>
                                  {jobOrder.client_complaints
                                    ? jobOrder.client_complaints.charAt(0).toUpperCase() +
                                    jobOrder.client_complaints.slice(1)
                                    : ""}
                                </TableCell>
                                <TableCell>
                                  {jobOrder.schedule_confirmed
                                    ? jobOrder.schedule_confirmed.charAt(0).toUpperCase() +
                                    jobOrder.schedule_confirmed.slice(1)
                                    : ""}
                                </TableCell>

                                <TableCell>
                                  <TextField
                                    multiline
                                    minRows={3}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={endorsement.landmark || ''}
                                    onChange={(e) =>
                                      handleEndorsementFieldChange(jobOrder.id, 'landmark', e.target.value)
                                    }
                                    inputProps={{ style: { fontSize: '0.8rem' } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    multiline
                                    minRows={3}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={endorsement.price || ''}
                                    onChange={(e) =>
                                      handleEndorsementFieldChange(jobOrder.id, 'price', e.target.value)
                                    }
                                    inputProps={{ style: { fontSize: '0.8rem' } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    multiline
                                    minRows={3}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={endorsement.required_chemical || ''}
                                    onChange={(e) =>
                                      handleEndorsementFieldChange(jobOrder.id, 'required_chemical', e.target.value)
                                    }
                                    inputProps={{ style: { fontSize: '0.8rem' } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    multiline
                                    minRows={3}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={endorsement.special_notes || ''}
                                    onChange={(e) =>
                                      handleEndorsementFieldChange(jobOrder.id, 'special_notes', e.target.value)
                                    }
                                    inputProps={{ style: { fontSize: '0.8rem' } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    multiline
                                    minRows={3}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={endorsement.number_of_visits_per_month || ''}
                                    onChange={(e) =>
                                      handleEndorsementFieldChange(jobOrder.id, 'number_of_visits_per_month', e.target.value)
                                    }
                                    inputProps={{ style: { fontSize: '0.8rem' } }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}

                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    mt={2}
                    mb={2}
                    mr={2}
                  >
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => navigate("/create-endorsement-form")}
                      sx={{
                        fontSize: "0.875rem",
                        color: "#FF4C4C",
                        borderColor: "#FF4C4C",
                      }}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      sx={{
                        marginLeft: "10px",
                        fontSize: "0.875rem",
                      }}
                      disabled={isLoading}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>

              </Grid>
            </Grid>

          </Grid>
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default SubmitEndorsementForm;
